import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ANGULAR_MODULES, OperationsComponent, SYNCFUSION_MODULES } from 'src/app/Shared/shared-imports';
import { TopBarComponent } from 'src/app/top-bar/top-bar.component';
import { PrinterSettingsService } from '../../../../services/printer-settings.service';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { SharedHTMLSettingsDataService } from 'src/app/Shared/sharedHTMLSettingsData.service';
import { Router } from '@angular/router';
import { IUserPermissionHandler } from 'src/app/Shared/model/CommonModel';
import { UserPermissionHandlerDirective } from 'src/app/Shared/userPermissionHandler.directive';
import { EActionTypes, EUserSecurityFunctionID, EToaster } from 'src/app/Shared/enum';
import { TooltipModule } from "@syncfusion/ej2-angular-popups";
import { LoaderComponent } from 'src/app/Shared/loader/loader.component';

@Component({
  selector: 'app-ops-printer-settings',
  standalone: true,
  imports: [TopBarComponent, SYNCFUSION_MODULES, ANGULAR_MODULES, OperationsComponent, UserPermissionHandlerDirective,
    TooltipModule, LoaderComponent],
  templateUrl: './ops-printer-settings.html',
  styleUrl: './ops-printer-settings.scss'
})
export class OpsPrinterSettings implements OnInit, OnDestroy {
  isCollapsed = true;
  public loaderIcon: Promise<boolean>;
  rentalForm!: FormGroup;
  posForm!: FormGroup;
  private destroy$ = new Subject<void>();
  thermalPrinters: any[] = [];
  originalRentalSettings: any;
  originalPOSSettings: any;

  viewPermissionHandlerData: IUserPermissionHandler = { FunctionId: EUserSecurityFunctionID.PrinterSettings, Action: EActionTypes.VIEW };
  createPermissionHandlerData: IUserPermissionHandler = { FunctionId: EUserSecurityFunctionID.PrinterSettings, Action: EActionTypes.CREATE };
  editPermissionHandlerData: IUserPermissionHandler = { FunctionId: EUserSecurityFunctionID.PrinterSettings, Action: EActionTypes.EDIT };

  constructor(
    private fb: FormBuilder,
    private printerService: PrinterSettingsService,
    private toastr: ToastrService,
    private sharedHTMLSettings: SharedHTMLSettingsDataService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.initForms();
    this.loadThermalPrinters();
    this.loadRentalSettings();
    this.loadPOSSettings();

    // Rental printer settings dependencies
    this.setupControlDependency('DailyThermal', ['DailyLogo', 'DailyCopies'], this.rentalForm);
    this.setupControlDependency('WeeklyThermal', ['WeeklyLogo', 'WeeklyCopies'], this.rentalForm);
    this.setupControlDependency('MonthlyThermal', ['MonthlyLogo', 'MonthlyCopies'], this.rentalForm);
    this.setupControlDependency('DeleteRentalThermal', ['DeleteRentalLogo', 'DeleteRentalCopies'], this.rentalForm);
    this.setupControlDependency('VoidRentalThermal', ['VoidRentalLogo', 'VoidRentalCopies'], this.rentalForm);
    this.setupControlDependency('PaymentThermal', ['PaymentLogo', 'PaymentCopies'], this.rentalForm);
    this.setupControlDependency('RentalMerchantCopy', ['RentalMerchantCopies'], this.rentalForm);
    this.setupControlDependency('RentalCustomerCopy', ['RentalCustomerCopies'], this.rentalForm);

    // POS printer settings dependencies
    this.setupControlDependency('RetailThermal', ['RetailLogo', 'RetailCopies'], this.posForm);
    this.setupControlDependency('WholesaleThermal', ['WholesaleLogo', 'WholesaleCopies'], this.posForm);
    this.setupControlDependency('OnlineSaleThermal', ['OnlineSaleLogo', 'OnlineSaleCopies'], this.posForm);
    this.setupControlDependency('PendingSaleThermal', ['PendingSaleLogo', 'PendingSaleCopies'], this.posForm);
    this.setupControlDependency('LayawayDownPaymentThermal', ['LayawayDownPaymentLogo', 'LayawayDownPaymentCopies'], this.posForm);
    this.setupControlDependency('LayawayInstallmentThermal', ['LayawayInstallmentLogo', 'LayawayInstallmentCopies'], this.posForm);
    this.setupControlDependency('ReturnSaleThermal', ['ReturnSaleLogo', 'ReturnSaleCopies'], this.posForm);
    this.setupControlDependency('VoidSaleThermal', ['VoidSaleLogo', 'VoidSaleCopies'], this.posForm);
    this.setupControlDependency('GcThermal', ['GcLogo', 'GcCopies'], this.posForm);
    this.setupControlDependency('PosMerchantCopy', ['PosMerchantCopies'], this.posForm);
    this.setupControlDependency('PosCustomerCopy', ['PosCustomerCopies'], this.posForm);
  }

  /** Toggle collapse */
  handleToggle(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  /** Initialize forms */
  private initForms(): void {
    this.rentalForm = this.fb.group({
      ReceiptDefaultChecked: [false],
      ContractDefaultChecked: [false],
      EmailReceipt: [false],
      EmailContract: [false],
      PrintReceipt: [false],
      PrintContract: [false],
      NumCopies: [1, Validators.required],
      DefaultRentalNotes: [''],
      RentalThermalPrinter: [null],
      DailyThermal: [false],
      DailyLogo: [false],
      DailyCopies: [null],
      WeeklyThermal: [false],
      WeeklyLogo: [false],
      WeeklyCopies: [null],
      MonthlyThermal: [false],
      MonthlyLogo: [false],
      MonthlyCopies: [null],
      DeleteRentalThermal: [false],
      DeleteRentalLogo: [false],
      DeleteRentalCopies: [null],
      VoidRentalThermal: [false],
      VoidRentalLogo: [false],
      VoidRentalCopies: [null],
      PaymentThermal: [false],
      PaymentLogo: [false],
      PaymentCopies: [null],
      RentalMerchantCopy: [false],
      RentalMerchantCopies: [null],
      RentalCustomerCopy: [false],
      RentalCustomerCopies: [null],
    });

    this.posForm = this.fb.group({
      PosThermalPrinter: [null],
      RetailThermal: [false],
      RetailLogo: [false],
      RetailCopies: [null],
      WholesaleThermal: [false],
      WholesaleLogo: [false],
      WholesaleCopies: [null],
      OnlineSaleThermal: [false],
      OnlineSaleLogo: [false],
      OnlineSaleCopies: [null],
      PendingSaleThermal: [false],
      PendingSaleLogo: [false],
      PendingSaleCopies: [null],
      LayawayDownPaymentThermal: [false],
      LayawayDownPaymentLogo: [false],
      LayawayDownPaymentCopies: [null],
      LayawayInstallmentThermal: [false],
      LayawayInstallmentLogo: [false],
      LayawayInstallmentCopies: [null],
      ReturnSaleThermal: [false],
      ReturnSaleLogo: [false],
      ReturnSaleCopies: [null],
      VoidSaleThermal: [false],
      VoidSaleLogo: [false],
      VoidSaleCopies: [null],
      GcThermal: [false],
      GcLogo: [false],
      GcCopies: [null],
      PosMerchantCopy: [false],
      PosMerchantCopies: [null],
      PosCustomerCopy: [false],
      PosCustomerCopies: [null],
      PosreceiptDefaultDisplayChecked: [false],
      PosreceiptDefaultPrintChecked: [false],
      PosreceiptDefaultEmailChecked: [false],
      ThermalFormatReceipt: [false]
    });
  }

  private setupControlDependency(
    parentControlName: string,
    childControlNames: string[],
    form: FormGroup
  ): void {
    const parentControl = form.get(parentControlName);

    if (!parentControl) return;

    parentControl.valueChanges.subscribe((enabled: boolean) => {
      childControlNames.forEach(childName => {
        const childControl = form.get(childName);
        if (childControl) {
          if (enabled) {
            childControl.enable({ emitEvent: false });
          } else {
            childControl.disable({ emitEvent: false });
            childControl.reset(); // optional: clears value when disabled
          }
        }
      });
    });

    // Run once on init to set correct state
    if (!parentControl.value) {
      childControlNames.forEach(childName => {
        const childControl = form.get(childName);
        if (childControl) {
          childControl.disable({ emitEvent: false });
        }
      });
    }
  }

  allowOnlyDigits(event: Event, maxDigits: number, form: FormGroup): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/\D/g, '');
    if (maxDigits && value.length > maxDigits) {
      value = value.substring(0, maxDigits);
    }

    input.value = value;

    const controlName = input.getAttribute('formControlName');
    if (controlName) {
      form.get(controlName)?.setValue(value ? Number(value) : null, { emitEvent: false });
    }
  }

  enforceMinCopies(): void {
    const control = this.rentalForm.get('NumCopies');
    let value = control?.value;

    if (!value || value < 1) {
      control?.setValue(1, { emitEvent: false });
    }
  }


  loadThermalPrinters(): void {
    this.printerService.getThermalPrinters().subscribe({
      next: (data) => {
        this.thermalPrinters = data;
      },
      error: (err) => {
        console.error('Failed to load thermal printers', err);
      }
    });
  }

  private loadRentalSettings(): void {
    this.printerService.getRentalPrinterSettings()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: data => {
          if (data) {
            this.rentalForm.patchValue(data);
            this.originalRentalSettings = { ...data }; // backup
          }
        },
        error: () => this.toastr.error(
          $localize`Error loading rental printer settings`,
          $localize`Error`,
          this.sharedHTMLSettings.ToasterConfig)
      });
  }

  private loadPOSSettings(): void {
    this.printerService.getPOSPrinterSettings()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: data => {
          if (data) {
            this.posForm.patchValue(data);
            this.originalPOSSettings = { ...data }; // backup
          }
        },
        error: () => this.toastr.error(
          $localize`Error loading POS printer settings`,
          $localize`Error`,
          this.sharedHTMLSettings.ToasterConfig)
      });
  }

  /** Save Rental */
  saveRental(): void {
    this.trimAllTextFields(this.rentalForm);

    if (this.rentalForm.invalid) {
      this.toastr.warning('Please fill required fields');
      return;
    }

    Swal.fire({
      title: 'Save Rental Printer Settings?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, Save'
    }).then(result => {
      if (result.isConfirmed) {
        this.loaderIcon = Promise.resolve(true);
        this.printerService.saveRentalPrinterSettings(this.rentalForm.value)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.loaderIcon = Promise.resolve(false);
              this.toastr.success(
                $localize`Rental Printer Settings saved successfully`,
                $localize`Success`,
                this.sharedHTMLSettings.ToasterConfig)
            },
            error: () => {
              this.loaderIcon = Promise.resolve(false);
              this.toastr.error(
                $localize`Error saving Rental Printer Settings`,
                $localize`Error`,
                this.sharedHTMLSettings.ToasterConfig)
            }
          });
      }
    });
  }

  /** Save POS */
  savePOS(): void {
    this.trimAllTextFields(this.posForm);

    if (this.posForm.invalid) {
      this.toastr.warning('Please fill required fields');
      return;
    }

    Swal.fire({
      title: 'Save POS Printer Settings?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, Save'
    }).then(result => {
      if (result.isConfirmed) {
        this.loaderIcon = Promise.resolve(true);
        this.printerService.savePOSPrinterSettings(this.posForm.value)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.loaderIcon = Promise.resolve(false);
              this.toastr.success(
                $localize`POS Printer Settings saved successfully`,
                $localize`Success`,
                this.sharedHTMLSettings.ToasterConfig
              )
            },
            error: () => {
              this.loaderIcon = Promise.resolve(false);
              this.toastr.error(
                $localize`Error saving POS Printer Settings`,
                $localize`Error`,
                this.sharedHTMLSettings.ToasterConfig
              )
            }
          });
      }
    });
  }

  save(): void {
    const activeTabId = this.getActiveTabId();

    if (activeTabId === 'rentalPrinterSettings') {
      this.saveRental();
    } else if (activeTabId === 'posPrinterSettings') {
      this.savePOS();
    }
  }

  getActiveTabId(): string {
    const activeTab = document.querySelector('.tab-pane.show.active');
    return activeTab ? activeTab.id : '';
  }

  cancelChanges(): void {
    const activeTabId = this.getActiveTabId();
    let form: FormGroup | null = null;
    let originalValues: any = null;

    switch (activeTabId) {
      case 'rentalPrinterSettings':
        form = this.rentalForm;
        originalValues = this.originalRentalSettings;
        break;

      case 'posPrinterSettings':
        form = this.posForm;
        originalValues = this.originalPOSSettings;
        break;

      default:
        console.warn('No active tab found for cancel.');
        return;
    }

    const navigateToDashboard = () => {
      if (originalValues) {
        form.reset(originalValues);
        form.markAsPristine();
      }
      this.router.navigate(['/dashboard']);
    };

    if (form?.dirty) {
      Swal.fire({
        title: $localize`Confirm`,
        text: $localize`Unsaved changes will be lost. Are you sure you want to continue?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: $localize`Yes`,
        cancelButtonText: $localize`No`,
      }).then(result => {
        if (result.isConfirmed) {
          navigateToDashboard();
        }
      });
    } else {
      navigateToDashboard();
    }
  }

  trimAllTextFields(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      if (control instanceof FormGroup) {
        this.trimAllTextFields(control);
      } else {
        if (typeof control?.value === 'string') {
          control.setValue(control.value.trim());
        }
      }
    });
  }

  onBeforeOpen(args: any): void {
    if (!args || !args.target || !args.target.dataset || !args.target.dataset.content) {
      args.cancel = true;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
